import React from 'react';

/**
 * TurfPulse™ Logo Component
 * 
 * Variants:
 * - icon: Just the pulse icon (animated or static)
 * - icon-filled: Icon with green background, white pulse
 * - icon-gradient: Icon with gradient
 * - horizontal: Icon + text side by side
 * - vertical: Icon + text stacked
 * 
 * Colors: 'green' | 'white' (for horizontal/vertical)
 * Animated: true | false (for icon only)
 */

export const TurfPulseLogo = ({ 
  variant = 'icon',
  color = 'green',
  animated = false, 
  size = 40, 
  className = "" 
}) => {
  const getLogoPath = () => {
    switch (variant) {
      case 'icon':
        return animated ? '/brand/turfpulse-logo-animated.svg' : '/brand/turfpulse-logo-static.svg';
      case 'icon-filled':
        return '/brand/turfpulse-icon-filled.svg';
      case 'icon-gradient':
        return '/brand/turfpulse-icon-gradient.svg';
      case 'horizontal':
        return color === 'white' ? '/brand/turfpulse-horizontal-white.svg' : '/brand/turfpulse-horizontal-green.svg';
      case 'vertical':
        return color === 'white' ? '/brand/turfpulse-vertical-white.svg' : '/brand/turfpulse-vertical-green.svg';
      default:
        return '/brand/turfpulse-logo-static.svg';
    }
  };
  
  const getWidth = () => {
    if (variant === 'horizontal') return size * 4;
    if (variant === 'vertical') return size * 2;
    return size;
  };
  
  const getHeight = () => {
    if (variant === 'horizontal') return size;
    if (variant === 'vertical') return size * 1.5;
    return size;
  };
  
  return (
    <img 
      src={getLogoPath()} 
      alt="TurfPulse™" 
      width={getWidth()} 
      height={getHeight()}
      className={className}
    />
  );
};

// Module-scope unique-id generator — keeps each animated logo instance from
// stomping on the others' keyframe class names without needing a React hook.
let _tpLogoUid = 0;
const _nextTpLogoUid = () => `tplogo-${++_tpLogoUid}`;

// SVG inline versions for better control
export const TurfPulseLogoSVG = ({ animated = false, size = 40, className = "" }) => {
  // EKG path — flat line with a clean spike near the center.
  // viewBox 100×100, circle r=42 at (50,50). Line enters left at x=8, exits right at x=92.
  // Spike: small dip → tall peak → deep valley → small rebound → flat.
  const EKG_PATH = "M 8 50 L 36 50 L 39 47 L 42 53 L 45 25 L 50 78 L 55 42 L 58 51 L 62 50 L 92 50";
  const STROKE = "#0D9256";
  // Stable per-instance id — generated once per mount via useRef
  const uidRef = React.useRef(null);
  if (uidRef.current === null) uidRef.current = _nextTpLogoUid();
  const uid = uidRef.current;

  if (animated) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        role="img"
        aria-label="TurfPulse logo"
      >
        <style>{`
          .tp-ring-${uid} {
            transform-origin: 50px 50px;
            animation: tp-beat-${uid} 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          }
          @keyframes tp-beat-${uid} {
            0%, 38%, 62%, 100% { transform: scale(1); opacity: 0.95; }
            48%                { transform: scale(1.04); opacity: 1; }
            52%                { transform: scale(1.04); opacity: 1; }
          }
          /* Bright trail chases left→right, then exits to reset */
          .tp-trail-${uid} {
            stroke-dasharray: 60 220;
            stroke-dashoffset: 60;
            animation: tp-trail-${uid} 2.2s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
            filter: drop-shadow(0 0 1.5px ${STROKE});
          }
          @keyframes tp-trail-${uid} {
            0%   { stroke-dashoffset: 60; }
            100% { stroke-dashoffset: -220; }
          }
          /* Scanner dot rides the path */
          .tp-cursor-${uid} {
            animation: tp-cursor-${uid} 2.2s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
            filter: drop-shadow(0 0 2px ${STROKE});
          }
          @keyframes tp-cursor-${uid} {
            0%   { opacity: 0; }
            6%   { opacity: 1; }
            94%  { opacity: 1; }
            100% { opacity: 0; }
          }
          @media (prefers-reduced-motion: reduce) {
            .tp-ring-${uid}, .tp-trail-${uid}, .tp-cursor-${uid} {
              animation: none !important;
            }
            .tp-trail-${uid} { stroke-dashoffset: 0; }
            .tp-cursor-${uid} { opacity: 0; }
          }
        `}</style>

        {/* Breathing ring */}
        <g className={`tp-ring-${uid}`}>
          <circle cx="50" cy="50" r="42" stroke={STROKE} strokeWidth="3" fill="none" />
        </g>

        {/* Dim rail — full path always visible */}
        <path
          d={EKG_PATH}
          stroke={STROKE}
          strokeOpacity="0.55"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* Bright trail — chases the cursor */}
        <path
          d={EKG_PATH}
          stroke={STROKE}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          className={`tp-trail-${uid}`}
        />
        {/* Scanner cursor — rides the path */}
        <circle r="1.8" fill={STROKE} className={`tp-cursor-${uid}`}>
          <animateMotion
            dur="2.2s"
            repeatCount="indefinite"
            path={EKG_PATH}
            keyPoints="0;1"
            keyTimes="0;1"
            calcMode="linear"
          />
        </circle>
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="TurfPulse logo"
    >
      <circle cx="50" cy="50" r="42" stroke={STROKE} strokeWidth="3" fill="none" />
      <path
        d={EKG_PATH}
        stroke={STROKE}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
};

// Usage Examples:
// <TurfPulseLogo variant="icon" animated={true} size={48} />
// <TurfPulseLogo variant="horizontal" color="green" size={40} />
// <TurfPulseLogo variant="vertical" color="white" size={60} />
// <TurfPulseLogo variant="icon-filled" size={64} />
// <TurfPulseLogo variant="icon-gradient" size={64} />
